import { Mail } from "lucide-react";
import { DashBoardItem, getTrackingStatusClass } from "./utilty";

function barColor(pct: number): string {
  if (pct === 100) return "#22C55E";
  if (pct > 0) return "#F8B71F";
  return "#E5E5EA";
}

interface ShipmentRowProps {
  item: DashBoardItem;
  checked: boolean;
  onRowClick: (item: DashBoardItem) => void;
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, item: DashBoardItem) => void;
}

export default function ShipmentRow({ item, checked, onRowClick, onCheckboxChange }: ShipmentRowProps) {
  return (
    <tr
      key={item.id}
      className={item?.isRead ? "" : "v3-unread"}
      onClick={() => onRowClick(item)}
    >
      {/* checkbox */}
      <td onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          className="v3-cb"
          checked={checked}
          onChange={e => onCheckboxChange(e, item)}
        />
      </td>

      {/* connection */}
      <td>
        <div className="v3-os">
          <span className={`v3-od ${item.isOnline ? "v3-od-on" : "v3-od-off"}`} />
          {item.isOnline ? "Online" : "Offline"}
        </div>
        <div className="v3-cs">{item?.airline || ""}</div>
      </td>

      {/* AWB */}
      <td>
        {/* <div className="v3-cm">{item?.mawb || ""}</div> */}
        <div className="v3-cm" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>{item?.mawb || ""}</span>
          {item?.trackingType && (
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 4,
              background: "#6366F115",
              color: "#6366F1",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              textTransform: "uppercase",
            }}>
              {item.trackingType}
            </span>
          )}
        </div>
        <div className="v3-cs">{item?.contactOrganisation || ""}</div>
        <div style={{ display: "flex", gap: "5px", marginTop: "4px" }}>
          {item.whatsappCount > 0 && (
            <span title={`${item.whatsappCount} WhatsApp`} style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              background: "#25D36615", color: "#25D366",
              borderRadius: 6, padding: "2px 6px", fontSize: 11, fontWeight: 500
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.855L0 24l6.335-1.508A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.877 9.877 0 01-5.031-1.378l-.36-.214-3.733.888.935-3.618-.235-.372A9.844 9.844 0 012.118 12C2.118 6.533 6.533 2.118 12 2.118c5.466 0 9.882 4.415 9.882 9.882 0 5.466-4.416 9.882-9.882 9.882z" />
              </svg>
            </span>
          )}
          {item.emailCount > 0 && (
            <span title={`${item.emailCount} Email`} style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              background: "#3B82F615", color: "#3B82F6",
              borderRadius: 6, padding: "2px 6px", fontSize: 11, fontWeight: 500
            }}>
              <Mail style={{ width: 12, height: 12 }} />
            </span>
          )}
        </div>
      </td>

      {/* route */}
      <td>
        <div className="v3-cm">{item.origin} → {item.destination}</div>
        {(item.pkts > 0 || item.weight > 0) && (
          <div className="v3-cs">{item.pkts} Pkts · {item.weight} Kgs</div>
        )}
      </td>

      {/* type */}
      <td>
        <span className={`v3-tt ${item.for === "air" ? "v3-tt-a" : "v3-tt-o"}`}>
          {item.for === "air" ? "Air" : "Ocean"}
        </span>
      </td>

      {/* status */}
      <td>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
          <span className={getTrackingStatusClass(item)}>
            <span className="v3-bdt" />
            {item.trackingStatus}
          </span>
          {item.shipmentActions.length > 0 && (
            <span className="action-taken-button">Action taken</span>
          )}
        </div>
      </td>

      {/* progress */}
      <td>
        <div className="v3-pg-group">
          {([["1st", item.first], ["Mid", item.middle], ["Last", item.last]] as [string, number][]).map(([lbl, pct]) => (
            <div className="v3-pr" key={lbl}>
              <span className="v3-pl">{lbl}</span>
              <div className="v3-pb">
                <div className="v3-pf" style={{ width: `${pct}%`, background: barColor(pct) }} />
              </div>
              <span className="v3-pp">{pct}%</span>
            </div>
          ))}
        </div>
      </td>

      {/* added date */}
      <td>
        <div className="v3-cm" style={{ fontWeight: 400, color: "#5A5A5E" }}>{item.createdAt}</div>
      </td>

      {/* action */}
      <td onClick={e => e.stopPropagation()}>
        <button className="v3-ab">
          <svg viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
        </button>
      </td>
    </tr>
  );
}